// How the signed-in account signs in (transparent authentication): the password it keeps, and the
// identity providers (Google, Microsoft, an organization's directory) it may connect or disconnect.
// Changing any of it needs a recent authentication: when the API asks for one, a dialog confirms
// it is the person (their password, or a trip to a provider they are connected with) and the
// change is made right after.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography
} from '@mui/material';
import { toast } from 'react-toastify';
import {
  authApi,
  CallerTokens,
  PasswordState,
  SignInMethod,
  SignInMethods as SignInMethodsPayload
} from '../../../../auth/authApi';
import { getPasswordPolicy, identityProvidersFor } from '../../../../auth/authMode';
import { passwordSatisfies } from '../../../../auth/passwordPolicy';
import {
  clearSession,
  freshAccessToken,
  hasSession,
  leaveTo,
  readSessionProfile,
  readTokens,
  storeSession
} from '../../../../auth/session';
import { startProviderSignIn } from '../../../../auth/sso';
import { codeOf, messageOf, ssoErrorMessage } from '../../../auth/authFlow';

interface SignInMethodsProps {
  /** the list arrived (or changed): the parent decides what else to show, e.g. the change-password form */
  onLoaded?: (data: SignInMethodsPayload) => void;
}

const PASSWORD_DETAIL: Record<PasswordState, string> = {
  set: 'You sign in with your password.',
  unset: 'No password yet. Set one to sign in without a provider.',
  none: 'No password yet. Set one to sign in without a provider.',
  elsewhere: 'A password account already exists for your email. Enter its password to connect it.'
};

/** Why there are no tokens to call with: the session is gone, or it could not be renewed just now. */
const noTokensMessage = (): string =>
  hasSession()
    ? "We couldn't confirm your session. Check your connection and reload the page."
    : 'Your session has ended. Sign in again to manage how you sign in.';

/** The tokens the signed-in calls identify themselves with (renewed first when about to expire). */
async function caller(): Promise<CallerTokens | null> {
  const accessToken = await freshAccessToken();
  const tokens = readTokens();
  return accessToken && tokens ? { accessToken, idToken: tokens.id_token, region: tokens.region } : null;
}

const Row: React.FC<{ testId: string; label: string; detail: string; main?: boolean; action?: React.ReactNode }> = ({
  testId,
  label,
  detail,
  main,
  action
}) => (
  <Box
    data-testid={testId}
    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1.5 }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="h6">{label}</Typography>
        {main ? (
          <Typography
            component="span"
            variant="caption"
            sx={{ px: 1, py: 0.25, borderRadius: '10px', border: '1px solid', borderColor: 'divider' }}
          >
            Main sign-in
          </Typography>
        ) : null}
      </Box>
      <Typography variant="body2" color="secondary.light">
        {detail}
      </Typography>
    </Box>
    <Box sx={{ flexShrink: 0 }}>{action}</Box>
  </Box>
);

const SignInMethods: React.FC<SignInMethodsProps> = ({ onLoaded }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [data, setData] = useState<SignInMethodsPayload | null>(null);
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState('');
  // The API asked for a recent authentication: once confirmed, the change is tried again.
  const [stepUp, setStepUp] = useState<{ retry: () => Promise<void> } | null>(null);
  const [stepUpPassword, setStepUpPassword] = useState('');
  const [stepUpError, setStepUpError] = useState('');
  const [passwordForm, setPasswordForm] = useState<'set' | 'connect' | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [existingPassword, setExistingPassword] = useState('');
  const [formError, setFormError] = useState('');
  const loaded = useRef(onLoaded);
  loaded.current = onLoaded;

  const email = readSessionProfile()?.user_metadata?.email || '';
  const providers = identityProvidersFor(email);
  const policy = getPasswordPolicy();
  // Where a provider transaction (connect, confirm) comes back to.
  const route = location.pathname;

  const load = useCallback(async () => {
    const tokens = await caller();
    if (!tokens) {
      setLoadError(noTokensMessage());
      return;
    }
    try {
      const next = await authApi.signInMethods(tokens);
      setData(next);
      setLoadError('');
      loaded.current?.(next);
    } catch (err) {
      setLoadError(messageOf(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Back from a provider transaction: say what happened, drop the query, show the list as it is now.
  useEffect(() => {
    const outcome = params.get('methods');
    const problem = params.get('methods_error');
    if (!outcome && !problem) return;
    if (outcome === 'connected') toast.success('Sign-in method connected.');
    if (outcome === 'confirmed')
      toast.success('Identity confirmed. You can change your sign-in methods for a few minutes.');
    if (problem) toast.error(ssoErrorMessage(problem));
    navigate(location.pathname, { replace: true });
    load();
  }, [params, navigate, location.pathname, load]);

  // Runs a change; a step-up refusal opens the confirm dialog, which runs the change again.
  const guarded = useCallback(async (key: string, action: () => Promise<void>): Promise<void> => {
    setBusy(key);
    try {
      await action();
    } catch (err) {
      if (codeOf(err) === 'reauth_required') setStepUp({ retry: () => guarded(key, action) });
      else toast.error(messageOf(err));
    } finally {
      setBusy('');
    }
  }, []);

  const connect = (connection: string) =>
    guarded(`connect:${connection}`, async () => {
      window.location.assign(await startProviderSignIn(connection, false, { purpose: 'link', returnTo: route }));
    });

  const disconnect = (method: SignInMethod) =>
    guarded(`disconnect:${method.userId}`, async () => {
      const tokens = await caller();
      if (!tokens) {
        toast.error(noTokensMessage());
        return;
      }
      const next = await authApi.unlink({ provider: method.provider, userId: method.userId }, tokens);
      setData(next);
      loaded.current?.(next);
      toast.success(`${method.label} disconnected.`);
    });

  const submitPassword = (event: React.FormEvent) => {
    event.preventDefault();
    if (passwordForm === 'set') {
      if (!passwordSatisfies(newPassword, policy)) return setFormError("Your password doesn't meet the requirements.");
      if (newPassword !== confirmPassword) return setFormError('The passwords differ.');
    } else if (!existingPassword) {
      return setFormError('Enter the password of your existing account.');
    }
    setFormError('');
    return guarded('password', async () => {
      const tokens = await caller();
      if (!tokens) {
        toast.error(noTokensMessage());
        return;
      }
      if (passwordForm === 'set') {
        const { session } = await authApi.setPassword({ newPassword }, tokens);
        // Every other session was signed out, this one with them, and no new one could be
        // opened: the person signs in again with the new password.
        if (!session) {
          clearSession();
          leaveTo(`${window.location.origin}/#/login?notice=changed`);
          return;
        }
        storeSession(session, tokens.region);
        toast.success('Password saved. Other devices were signed out.');
      } else {
        const next = await authApi.connectPassword({ password: existingPassword }, tokens);
        setData(next);
        loaded.current?.(next);
        toast.success('Password connected.');
      }
      setPasswordForm(null);
      setNewPassword('');
      setConfirmPassword('');
      setExistingPassword('');
      await load();
    });
  };

  const confirmWithPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stepUp) return;
    const tokens = await caller();
    if (!tokens) return setStepUpError(noTokensMessage());
    try {
      await authApi.reauthenticate({ password: stepUpPassword }, tokens);
      const { retry } = stepUp;
      setStepUp(null);
      setStepUpPassword('');
      setStepUpError('');
      await retry();
    } catch (err) {
      setStepUpError(messageOf(err));
      setStepUpPassword('');
    }
  };

  const confirmWithProvider = async (connection: string) => {
    try {
      window.location.assign(await startProviderSignIn(connection, false, { purpose: 'reauth', returnTo: route }));
    } catch (err) {
      setStepUpError(messageOf(err));
    }
  };

  const password = data?.methods.find((m) => m.provider === 'auth0') ?? null;
  const connectedProviders = data?.methods.filter((m) => m.provider !== 'auth0') ?? [];
  // Providers this deployment offers, plus anything else the account is connected with.
  const rows = [
    ...providers.map((p) => ({
      connection: p.connection,
      label: p.label,
      method: connectedProviders.find((m) => m.connection === p.connection) ?? null
    })),
    ...connectedProviders
      .filter((m) => !providers.some((p) => p.connection === m.connection))
      .map((m) => ({ connection: m.connection, label: m.label, method: m }))
  ];

  return (
    <>
      <Box p={2}>
        <Typography variant="h5">Sign-in methods</Typography>
      </Box>
      <Divider />
      <Box px={2} py={1} data-testid="sign-in-methods">
        {loadError ? (
          <Typography color="error" role="alert" py={1.5}>
            {loadError}
          </Typography>
        ) : !data ? (
          <Typography color="secondary.light" py={1.5}>
            Loading…
          </Typography>
        ) : (
          <>
            <Row
              testId="sign-in-method-password"
              label="Password"
              detail={PASSWORD_DETAIL[data.passwordState]}
              main={password?.main}
              action={
                data.passwordState === 'set' ? null : data.passwordState === 'elsewhere' ? (
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={busy !== ''}
                    onClick={() => setPasswordForm('connect')}
                  >
                    Connect password
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    size="small"
                    disabled={busy !== ''}
                    onClick={() => setPasswordForm('set')}
                  >
                    Set a password
                  </Button>
                )
              }
            />
            {passwordForm ? (
              <Box component="form" onSubmit={submitPassword} noValidate sx={{ pb: 2, maxWidth: 420 }}>
                {passwordForm === 'set' ? (
                  <>
                    <TextField
                      type="password"
                      label="New password"
                      size="small"
                      fullWidth
                      autoFocus
                      autoComplete="new-password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      sx={{ mb: 1.5 }}
                    />
                    <TextField
                      type="password"
                      label="Confirm new password"
                      size="small"
                      fullWidth
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      sx={{ mb: 1.5 }}
                    />
                  </>
                ) : (
                  <TextField
                    type="password"
                    label="Password of your existing account"
                    size="small"
                    fullWidth
                    autoFocus
                    autoComplete="current-password"
                    value={existingPassword}
                    onChange={(e) => setExistingPassword(e.target.value)}
                    sx={{ mb: 1.5 }}
                  />
                )}
                {formError ? (
                  <Typography color="error" variant="body2" role="alert" sx={{ mb: 1 }}>
                    {formError}
                  </Typography>
                ) : null}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button type="submit" variant="contained" size="small" disabled={busy !== ''}>
                    {busy === 'password' ? 'Saving…' : passwordForm === 'set' ? 'Save password' : 'Connect'}
                  </Button>
                  <Button size="small" onClick={() => setPasswordForm(null)} disabled={busy !== ''}>
                    Cancel
                  </Button>
                </Box>
              </Box>
            ) : null}
            {rows.map(({ connection, label, method }) => (
              <React.Fragment key={connection}>
                <Divider />
                <Row
                  testId={`sign-in-method-${connection}`}
                  label={label}
                  detail={method ? `Connected${method.email ? ` as ${method.email}` : ''}.` : 'Not connected.'}
                  main={method?.main}
                  action={
                    method ? (
                      method.removable ? (
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={busy !== ''}
                          onClick={() => disconnect(method)}
                        >
                          {busy === `disconnect:${method.userId}` ? 'Disconnecting…' : 'Disconnect'}
                        </Button>
                      ) : null
                    ) : (
                      <Button
                        variant="contained"
                        size="small"
                        disabled={busy !== ''}
                        onClick={() => connect(connection)}
                      >
                        {busy === `connect:${connection}` ? 'Opening…' : 'Connect'}
                      </Button>
                    )
                  }
                />
              </React.Fragment>
            ))}
          </>
        )}
      </Box>

      <Dialog open={stepUp !== null} onClose={() => setStepUp(null)} fullWidth maxWidth="xs">
        <DialogTitle>Confirm it&apos;s you</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="secondary.light" sx={{ mb: 2 }}>
            Changing how you sign in needs a recent sign-in.
          </Typography>
          {data?.hasPassword ? (
            <Box component="form" onSubmit={confirmWithPassword} noValidate>
              <TextField
                type="password"
                label="Your password"
                size="small"
                fullWidth
                autoFocus
                autoComplete="current-password"
                value={stepUpPassword}
                onChange={(e) => setStepUpPassword(e.target.value)}
              />
              <Button type="submit" variant="contained" size="small" sx={{ mt: 1.5 }}>
                Confirm
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gap: 1 }}>
              {connectedProviders.map((m) => (
                <Button
                  key={m.connection}
                  variant="outlined"
                  size="small"
                  onClick={() => confirmWithProvider(m.connection)}
                >
                  Confirm with {m.label}
                </Button>
              ))}
            </Box>
          )}
          {stepUpError ? (
            <Typography color="error" variant="body2" role="alert" sx={{ mt: 1.5 }}>
              {stepUpError}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStepUp(null)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SignInMethods;
